import React, { useState } from 'react';
import { 
  XMarkIcon,
  DocumentDuplicateIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ApiRequest, generateCodeSnippet } from '../../services/apiPlaygroundService';

interface CodeGeneratorProps {
  request: ApiRequest;
  apiKey: string;
  onClose: () => void;
}

type Language = 'curl' | 'python' | 'javascript' | 'nodejs';

const CodeGenerator: React.FC<CodeGeneratorProps> = ({ request, apiKey, onClose }) => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('curl');
  const [copied, setCopied] = useState(false);

  const languages = [
    { id: 'curl' as Language, name: 'cURL', syntax: 'bash' },
    { id: 'python' as Language, name: 'Python', syntax: 'python' },
    { id: 'javascript' as Language, name: 'JavaScript', syntax: 'javascript' },
    { id: 'nodejs' as Language, name: 'Node.js', syntax: 'javascript' },
  ];

  const currentLanguage = languages.find(lang => lang.id === selectedLanguage);
  const code = generateCodeSnippet(request, apiKey, selectedLanguage);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const getLanguageDescription = (language: Language) => {
    switch (language) {
      case 'curl':
        return 'Command-line tool for making HTTP requests. Works on most Unix-like systems.';
      case 'python':
        return 'Python code using the requests library. Install with: pip install requests';
      case 'javascript':
        return 'Modern JavaScript using fetch API. Works in browsers and modern Node.js.';
      case 'nodejs':
        return 'Node.js code using axios library. Install with: npm install axios';
      default:
        return '';
    }
  };

  const getInstallationCommand = (language: Language) => {
    switch (language) {
      case 'python':
        return 'pip install requests';
      case 'nodejs':
        return 'npm install axios';
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Code Generator</h3>
            <p className="text-sm text-gray-500">
              Generate code snippets for your API request
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Language Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Language
          </label>
          <div className="flex flex-wrap gap-2">
            {languages.map((language) => (
              <button
                key={language.id}
                onClick={() => setSelectedLanguage(language.id)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedLanguage === language.id
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                {language.name}
              </button>
            ))}
          </div>
        </div>

        {/* Language Description */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            {getLanguageDescription(selectedLanguage)}
          </p>
          {getInstallationCommand(selectedLanguage) && (
            <div className="mt-2">
              <p className="text-xs text-blue-700 font-medium">Installation:</p>
              <code className="text-xs bg-blue-100 px-2 py-1 rounded text-blue-800">
                {getInstallationCommand(selectedLanguage)}
              </code>
            </div>
          )}
        </div>

        {/* Code Display */}
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Generated Code
            </label>
            <button
              onClick={copyToClipboard}
              className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {copied ? (
                <>
                  <CheckIcon className="h-4 w-4 mr-1 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                  Copy
                </>
              )}
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <SyntaxHighlighter
              language={currentLanguage?.syntax || 'bash'}
              style={tomorrow}
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '0.875rem',
                lineHeight: '1.5',
              }}
              showLineNumbers={true}
              wrapLines={true}
            >
              {code}
            </SyntaxHighlighter>
          </div>
        </div>

        {/* Request Details */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Request Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Method:</span>
              <span className="ml-2 text-gray-900">{request.method}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">URL:</span>
              <span className="ml-2 text-gray-900 break-all">{request.url}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Headers:</span>
              <span className="ml-2 text-gray-900">{Object.keys(request.headers).length} headers</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Body:</span>
              <span className="ml-2 text-gray-900">
                {request.body ? `${request.body.length} characters` : 'No body'}
              </span>
            </div>
          </div>
        </div>

        {/* Usage Notes */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Usage Notes</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Replace the API key with your actual key before using</li>
            <li>• Make sure to handle errors appropriately in production code</li>
            <li>• Consider implementing retry logic for network failures</li>
            <li>• Store API keys securely (environment variables, not in code)</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeGenerator;
